/**
 * Client-side open/closed recomputation.
 *
 * The server renders the correct status, but store pages sit behind WP Rocket /
 * templ_cache — a cached HTML response would otherwise freeze "Öppet nu" at
 * whatever the cache warmer saw. This re-runs the same rules in the browser and
 * corrects the label if it's stale. Without JavaScript the server-rendered
 * value simply stands.
 *
 * The seven rules below mirror StoreHours::status() in
 * app/controllers/StoreHours.controller.php exactly. Change one, change both.
 * The fixtures in ".docs/versa/ai-notes/tests/store-hours-fixtures.php" cover every branch.
 *
 * Usage (Alpine 2 — this project bundles 2.8.2, so no Alpine.data()):
 *
 *   <div x-data="versaStoreStatus('vapes-store-hours-goteborg')" x-init="recompute()">
 *     <span data-status-pill>
 *       <span data-status-dot></span>
 *       <span data-status-label>Öppet nu</span>
 *       <span data-status-sub>Stänger 19:00</span>
 *     </span>
 *   </div>
 *
 * Note the deliberate absence of x-text: recompute() writes textContent itself,
 * so when the payload is missing or malformed it changes nothing and the
 * server-rendered label stands. An x-text binding would blank the label in
 * exactly the cases where the fallback matters most.
 */

var DAY_SHORT = [ 'mån', 'tis', 'ons', 'tor', 'fre', 'lör', 'sön' ];
var CLOSING_SOON_MINUTES = 60;
var LOOKAHEAD_DAYS = 14;

/**
 * "Now" as a civil date in the given timezone: what a wall clock in that city
 * shows, independent of the visitor's own timezone.
 *
 * @param {string} tz IANA timezone.
 * @return {{y:number, m:number, d:number, mins:number}}
 */
function civilNow( tz ) {
	var now = new Date();

	try {
		var parts = new Intl.DateTimeFormat( 'en-CA', {
			timeZone: tz,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
		} ).formatToParts( now );

		var get = function ( type ) {
			for ( var i = 0; i < parts.length; i++ ) {
				if ( parts[ i ].type === type ) {
					return parseInt( parts[ i ].value, 10 );
				}
			}
			return 0;
		};

		var hour = get( 'hour' );
		// Some engines render midnight as 24 in hour12:false.
		if ( 24 === hour ) {
			hour = 0;
		}

		return { y: get( 'year' ), m: get( 'month' ), d: get( 'day' ), mins: hour * 60 + get( 'minute' ) };
	} catch ( e ) {
		// Unknown timezone: fall back to the visitor's own clock rather than
		// showing nothing.
		return {
			y: now.getFullYear(),
			m: now.getMonth() + 1,
			d: now.getDate(),
			mins: now.getHours() * 60 + now.getMinutes(),
		};
	}
}

/** ISO weekday index, 0 = Monday (matching the stored hours array). */
function dayIndex( civil ) {
	return ( new Date( Date.UTC( civil.y, civil.m - 1, civil.d ) ).getUTCDay() + 6 ) % 7;
}

/** YYYY-MM-DD. */
function ymd( civil ) {
	var pad = function ( n ) {
		return ( n < 10 ? '0' : '' ) + n;
	};
	return civil.y + '-' + pad( civil.m ) + '-' + pad( civil.d );
}

/** A civil date N days later, keeping the same time-of-day. */
function addDays( civil, n ) {
	var date = new Date( Date.UTC( civil.y, civil.m - 1, civil.d ) );
	date.setUTCDate( date.getUTCDate() + n );
	return {
		y: date.getUTCFullYear(),
		m: date.getUTCMonth() + 1,
		d: date.getUTCDate(),
		mins: civil.mins,
	};
}

/** Minutes since midnight for "HH:MM", or null. */
function minutes( hhmm ) {
	var match = /^(\d{2}):(\d{2})$/.exec( hhmm || '' );
	return match ? parseInt( match[ 1 ], 10 ) * 60 + parseInt( match[ 2 ], 10 ) : null;
}

/** Rule 2: a holiday entry for this date wins over the weekly row. */
function resolveDay( payload, civil ) {
	var date = ymd( civil );
	var holidays = payload.holidays || [];

	for ( var i = 0; i < holidays.length; i++ ) {
		if ( holidays[ i ].date !== date ) {
			continue;
		}
		var holiday = holidays[ i ];
		if ( ! holiday.closed && holiday.open && holiday.close && holiday.close > holiday.open ) {
			return { closed: false, open: holiday.open, close: holiday.close };
		}
		return { closed: true };
	}

	var day = ( payload.hours || [] )[ dayIndex( civil ) ];
	if ( ! day || day.closed || ! day.open || ! day.close ) {
		return { closed: true };
	}
	return day;
}

/** Rule 6: the next opening within the lookahead window, or ''. */
function nextOpening( payload, civil ) {
	for ( var i = 1; i <= LOOKAHEAD_DAYS; i++ ) {
		var date = addDays( civil, i );
		var day = resolveDay( payload, date );
		if ( day.closed ) {
			continue;
		}
		if ( 1 === i ) {
			return 'Öppnar imorgon ' + day.open;
		}
		return 'Öppnar ' + DAY_SHORT[ dayIndex( date ) ] + ' ' + day.open;
	}
	return '';
}

/**
 * Open/closed status. Mirrors StoreHours::status().
 *
 * @param {object} payload {tz, state, hours, holidays}
 * @return {{state:string, label:string, sub:string}}
 */
function statusFor( payload ) {
	// 1. An explicit non-open store state short-circuits everything.
	if ( 'permanently_closed' === payload.state ) {
		return { state: 'closed', label: 'Permanent stängt', sub: '' };
	}
	if ( 'temporarily_closed' === payload.state ) {
		return { state: 'closed', label: 'Tillfälligt stängt', sub: '' };
	}

	var civil = civilNow( payload.tz || 'Europe/Stockholm' );
	var today = resolveDay( payload, civil );

	if ( ! today.closed ) {
		var open = minutes( today.open );
		var close = minutes( today.close );

		// 3. Not open yet today.
		if ( null !== open && civil.mins < open ) {
			return { state: 'closed', label: 'Stängt', sub: 'Öppnar idag ' + today.open };
		}

		if ( null !== close && civil.mins < close ) {
			// 4. Lunch break.
			var breakStart = minutes( today.break_start );
			var breakEnd = minutes( today.break_end );
			if ( null !== breakStart && null !== breakEnd && civil.mins >= breakStart && civil.mins < breakEnd ) {
				return { state: 'closed_break', label: 'Lunchstängt', sub: 'Öppnar igen ' + today.break_end };
			}

			// 5. Closing soon.
			if ( close - civil.mins <= CLOSING_SOON_MINUTES ) {
				return { state: 'closing_soon', label: 'Öppet — stänger snart', sub: 'Stänger ' + today.close };
			}

			// 7. Open.
			return { state: 'open', label: 'Öppet nu', sub: 'Stänger ' + today.close };
		}
	}

	// 6. Closed for the day.
	return { state: 'closed', label: 'Stängt', sub: nextOpening( payload, civil ) };
}

/**
 * Alpine component. Reads its data from the inline JSON the server printed
 * next to the pill, so no request is made and the payload stays under 1 KB.
 */
window.versaStoreStatus = function ( payloadId ) {
	return {
		state: '',
		label: '',
		sub: '',

		recompute: function () {
			var node = document.getElementById( payloadId );
			if ( ! node ) {
				return;
			}

			var payload;
			try {
				payload = JSON.parse( node.textContent );
			} catch ( e ) {
				// Leave the server-rendered markup alone on malformed JSON.
				return;
			}

			var status = statusFor( payload );
			this.state = status.state;
			this.label = status.label;
			this.sub = status.sub;

			var labelNode = this.$el.querySelector( '[data-status-label]' );
			var subNode = this.$el.querySelector( '[data-status-sub]' );
			if ( labelNode ) {
				labelNode.textContent = status.label;
			}
			if ( subNode ) {
				subNode.textContent = status.sub;
				// A closed store with no next opening inside the lookahead
				// window has nothing to say — hide the separator too.
				subNode.hidden = ! status.sub;
			}

			// Status colours live in PHP as complete literal class strings
			// (Tailwind only sees verbatim names), so swap the whole set here.
			var palette = {
				open: [ 'bg-green-50', 'text-green-800', 'bg-green-600' ],
				closing_soon: [ 'bg-amber-50', 'text-amber-800', 'bg-amber-500' ],
				closed_break: [ 'bg-amber-50', 'text-amber-800', 'bg-amber-500' ],
				closed: [ 'bg-gray-100', 'text-gray-600', 'bg-gray-400' ],
			};
			var all = [ 'bg-green-50', 'text-green-800', 'bg-green-600', 'bg-amber-50', 'text-amber-800', 'bg-amber-500', 'bg-gray-100', 'text-gray-600', 'bg-gray-400' ];
			var next = palette[ status.state ] || palette.closed;

			var pill = this.$el.querySelector( '[data-status-pill]' );
			var dot = this.$el.querySelector( '[data-status-dot]' );
			if ( pill ) {
				pill.classList.remove.apply( pill.classList, all );
				pill.classList.add( next[ 0 ], next[ 1 ] );
			}
			if ( dot ) {
				dot.classList.remove.apply( dot.classList, all );
				dot.classList.add( next[ 2 ] );
			}
		},
	};
};

// Exported for the fixture harness and for any non-Alpine caller.
window.versaStoreStatusFor = statusFor;
